import { defineStore } from 'pinia'
import Keycloak, { type KeycloakInitOptions } from 'keycloak-js'
import { useStorage } from '@vueuse/core'
import { type RemovableRef } from '@vueuse/shared'
import { OrganizationMs, useFetchApiRequestAsync } from 'auth-package'
import { EConsoleType, useConsoleMessage } from '@/composables/useConsoleMessage'

export enum OrganizationScope {
    CREATE = 'create',
    READ = 'read',
    UPDATE = 'update',
    DELETE = 'delete'
}

//Initiate Keycloak for access authentication and authorizations
let kc: Keycloak

function keycloakSingleton(): Keycloak {
    if (!kc) {
        kc = new Keycloak({
            url: 'http://localhost:8090',
            realm: 'prizific-admin',
            clientId: 'admin-auth'
        })
    }

    return kc
}

interface PersistentDetails {
    token?: string
    refreshToken?: string
    activeOrganization?: Organization
}

interface AuthState {
    persistentDetails: RemovableRef<PersistentDetails>
    authenticated: boolean
    initialized: boolean
    user: any

    organizations: { [key: string]: Organization }
    //campaignIds: Array<any>
    locked: boolean
    lastActivity: number
    remainingSecToLockDown: number
    remainingSecToLogOut: number
    lockedDown: boolean
    sessionActive: boolean
}

export interface Organization {
    uuid: string
    name: string
    icon: string
    description: string
    organizationId: string
    organizationName: string
}

/**
 * @deprecated
 */
export const useAuthStore = defineStore('auth', {
    state: (): AuthState => {
        return {
            persistentDetails: useStorage<PersistentDetails>('authState', {}),
            authenticated: false,
            initialized: false,
            user: undefined,
            organizations: {},
            locked: false,
            remainingSecToLockDown: 300,
            remainingSecToLogOut: 1500,
            lastActivity: 0,
            lockedDown: false,
            sessionActive: false
        }
    },
    getters: {
        activeOrganization(state): Organization | undefined {
            console.log(
                'state.persistentDetails.activeOrganization',
                state.persistentDetails.activeOrganization
            )
            if (state.persistentDetails.activeOrganization !== undefined) {
                return state.persistentDetails.activeOrganization
            }
            const keys = Object.keys(state.organizations)
            console.log('state.organizations[0]', keys)

            if (keys.length > 0) {
                return state.organizations[keys[0]] as any
            }
            return undefined
        },
        isActiveCompany() {
            return (organization: Organization): boolean => {
                return this.activeOrganization?.organizationId == organization.organizationId
            }
        }
    },
    actions: {
        //Initiate login, and gen necessary information about the current login state
        async init() {
            if (this.initialized) {
                return
            }

            this.initialized = true

            keycloakSingleton().onReady = () => {
                console.log('KC onReady')
            }
            keycloakSingleton().onAuthSuccess = () => {
                console.log('KC onAuthSuccess')
                this.sessionActive = true
                this.persistentDetails.token = kc.token
                this.persistentDetails.refreshToken = kc.refreshToken
            }
            keycloakSingleton().onAuthError = () => {
                console.log('KC onAuthError ')
                this.sessionActive = false
            }
            keycloakSingleton().onAuthRefreshSuccess = () => {
                console.log('KC onAuthRefreshSuccess')
                this.sessionActive = true
                this.persistentDetails.token = kc.token
                this.persistentDetails.refreshToken = kc.refreshToken
            }
            keycloakSingleton().onAuthRefreshError = () => {
                console.log('KC onAuthRefreshError')
                this.sessionActive = false
            }
            keycloakSingleton().onAuthLogout = () => {
                console.log('KC onAuthLogout')
                this.sessionActive = false
            }
            keycloakSingleton().onTokenExpired = async () => {
                //When the token is expired show shut down
                this.showLockDown()
            }
            const keycloakOptions: KeycloakInitOptions = {
                onLoad: 'login-required'
            }
            //Load Default State
            if (this.persistentDetails.token) {
                keycloakOptions.token = this.persistentDetails.token
            }

            if (this.persistentDetails.refreshToken) {
                keycloakOptions.refreshToken = this.persistentDetails.refreshToken
            }

            const authenticated = await keycloakSingleton().init(keycloakOptions)

            if (authenticated) {
                this.authenticated = true

                this.user = (await kc.loadUserProfile()) as any

                const userInfo = (await kc.loadUserInfo()) as any

                this.organizations = userInfo.organizations ?? []

                this.handleActivity()
            } else {
                this.authenticated = false
            }
        },
        handleActivity() {
            //The function that will be called whenever a user is active
            const activity = () => {
                //reset the secondsSinceLastActivity variable
                //back to 0
                this.lastActivity = 0
            }

            //An array of DOM events that should be interpreted as
            //user activity.
            const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart']

            //add these events to the document.
            //register the activity function as the listener parameter.
            activityEvents.forEach(function (eventName) {
                document.addEventListener(eventName, activity, true)
            })

            const checkingInterval = setInterval(() => {
                this.lastActivity++

                if (navigator.userActivation.isActive) {
                    activity()
                }

                const keycloak = keycloakSingleton()

                const tokenLifetime =
                    (keycloak.tokenParsed?.exp ?? 0) - (keycloak.tokenParsed?.iat ?? 0)

                let refreshTokenLifetime =
                    (keycloak.refreshTokenParsed?.exp ?? 0) - (keycloak.tokenParsed?.iat ?? 0)
                refreshTokenLifetime = 130
                if (!this.lockedDown && tokenLifetime >= this.lastActivity) {
                    this.refreshToken(10)
                }

                if (refreshTokenLifetime - 20 < this.lastActivity) {
                    clearInterval(checkingInterval)
                    //If the refresh token is expired we have obtain a new Token, for this we have to refresh tha page.
                    this.sessionActive = false
                }
            }, 1000)
        },
        refreshToken(gap: number) {
            keycloakSingleton().updateToken(gap)
        },

        showLockDown() {
            this.lockedDown = true
        },
        async logout() {
            await keycloakSingleton().logout()
        },

        async testCompany(companyId: string) {
            fetch('http://localhost:3012/company/' + companyId, {
                headers: {
                    Authorization: 'Bearer ' + keycloakSingleton().token
                }
            })
        },

        async testUnprotected(param: string) {
            fetch('http://localhost:3012/uprotected/' + param, {
                headers: {
                    Authorization: 'Bearer ' + keycloakSingleton().token
                }
            })
        },
        async selectActiveOrganizationById(organizationId: string) {
            const organization = this.organizations[organizationId]

            if (organization) {
                this.selectActiveOrganization(organization)
            }
        },
        async selectActiveOrganization(organization: Organization) {
            this.persistentDetails.activeOrganization = organization
        }
    }
})
